import { CheckCircleIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { PropsWithChildren } from "react";

import { Small } from "@fronterahealth/frontera-ui-components";
import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { CommentActionTooltip } from "@components/QaReview/components/CommentActionTooltip";
import { ResolutionLabel } from "@components/QaReview/components/ResolutionLabel";

interface ResolvedCommentSummaryProps {
  comment: ReviewItemFieldsFragment;
  expanded: boolean;
  onToggleExpanded: () => void;
  isActive?: boolean;
  /** Goes to the comment's field; the summary calls it when opened or collapsed, as clicking the card does. */
  onActivate?: () => void;
}

export const ResolvedCommentSummary: React.FC<PropsWithChildren<ResolvedCommentSummaryProps>> = ({
  comment,
  expanded,
  onToggleExpanded,
  isActive = false,
  onActivate,
  children,
}) => {
  // The summary covers the card, so opening or collapsing it also goes to the field.
  const toggle = () => {
    onToggleExpanded();
    onActivate?.();
  };

  if (expanded) {
    return (
      <div className="space-y-1">
        <CommentActionTooltip label="Fold this resolved comment back into a one-line summary">
          <button
            type="button"
            className="text-xs font-medium text-text-tertiary hover:text-text-secondary"
            onClick={toggle}
          >
            Collapse
          </button>
        </CommentActionTooltip>
        {children}
      </div>
    );
  }

  return (
    <button
      type="button"
      data-dd-action-name="resolved comment summary"
      onClick={toggle}
      // Shifts like an active card, so stepping onto a resolved item with the navigator shows where you are.
      className={cn(
        "flex w-full items-center gap-2 rounded-md border border-limestone-150 bg-limestone-50 px-3 py-1.5 text-left transition-all duration-150 hover:bg-limestone-100",
        isActive && "shadow-xl -translate-x-2",
      )}
    >
      <CheckCircleIcon className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
      <Small className="min-w-0 flex-1 truncate text-text-tertiary">
        <span className="font-medium">
          <ResolutionLabel comment={comment} />
        </span>
        {comment.__typename !== "ReportCommentType" && " · Quality Review"} —{" "}
        {comment.__typename === "ReportCommentType" && comment.isDeleted ? "Comment deleted" : comment.content ?? ""}
      </Small>
      <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
    </button>
  );
};
